/* @flow */
import _ from 'lodash';
import cx from 'classnames';
import * as React from 'react';
import Modal from 'react-modal';

import ConfirmDialog from 'components/ConfirmDialog';
import CurrentUser from 'auth/CurrentUser';
import Glyph from 'components/Glyph';
import Menu from 'components/Menu';
import MenuItem from 'components/MenuItem';
import Text from 'components/Text';
import Image from 'components/Image';
import MediaObject from 'components/MediaObject';
import { patchRoles, patchCurrentUserRoles } from 'admin/actions/OrgRoleActions';
import { rolesFlags, roleListFromFlags, roleToIcon } from 'utils/roleHelpers';
import errorLogger from 'utils/errorLogger';

import type { PotentialRole } from 'groups/components/OrgRoleDropdown';
import type { RoleFlags } from 'utils/roleHelpers';
import type { OrganizationRole } from 'queries/__generated__/globalGQLTypes';

import './styles/OrgRoleDropdownMenu.scss';

type Props = {
  organizationId: number,
  roles: OrganizationRole[],
  potentialRoles: PotentialRole[],
  userId: Backbone$Id,
  isCurrentUser?: boolean,
  onClose?: (roles: OrganizationRole[]) => void,
  onFinishSave?: () => void,
  onRequestHide?: () => void,
};

type State = {
  showConfirmModal: boolean,
};

class OrgRoleDropdownMenu extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.checkedRoles = rolesFlags(props.roles);
    this.state = {
      showConfirmModal: false,
    };
  }

  componentWillReceiveProps(nextProps: Props): void {
    if (!_.isEqual(this.props.roles, nextProps.roles)) {
      this.checkedRoles = rolesFlags(nextProps.roles);
    }
  }

  checkedRoles: RoleFlags;

  commitPatchRole = () => {
    const { organizationId, userId, onClose } = this.props;
    const checkedRolesList = roleListFromFlags(this.checkedRoles);

    const patchFn = this.props.isCurrentUser ? patchCurrentUserRoles : patchRoles;

    // $FlowFixMe
    patchFn(organizationId, userId, checkedRolesList)
      .then(() => {
        const { onFinishSave } = this.props;
        if (onFinishSave) {
          onFinishSave();
        }
      })
      .catch(errorLogger('OrgRoleDropdownMenu handleClose patch failed'));

    if (onClose) {
      onClose(checkedRolesList);
    }

    this.setState({ showConfirmModal: false });

    if (this.props.onRequestHide) {
      this.props.onRequestHide();
    }
  };

  revertPatchRole = () => {
    this.checkedRoles = rolesFlags(this.props.roles);
    this.setState({ showConfirmModal: false });
    if (this.props.onRequestHide) {
      this.props.onRequestHide();
    }
  };

  handleClose = () => {
    const { userId, roles } = this.props;
    const checkedRolesList = roleListFromFlags(this.checkedRoles);

    if (_.xor(roles, checkedRolesList).length) {
      if (CurrentUser.getUserId() === userId) {
        this.setState({ showConfirmModal: true });
      } else {
        this.commitPatchRole();
      }
    } else if (this.props.onRequestHide) {
      this.props.onRequestHide();
    }
  };

  renderMenuItem(role: string, roleName: string, icon: string) {
    const checked = this.checkedRoles[role];
    const disabled = role === 'admin' && this.checkedRoles.admin;

    const handleClick = () => {
      const nextRoles = { ...this.checkedRoles };
      nextRoles[role] = !this.checkedRoles[role];
      if (role === 'student' && nextRoles.student) {
        nextRoles.parent = false;
        nextRoles.teacher = false;
        nextRoles.staff = false;
      } else if (nextRoles.teacher || nextRoles.parent || nextRoles.admin || nextRoles.staff) {
        nextRoles.student = false;
      }

      if (role === 'admin' && !this.checkedRoles.parent) {
        nextRoles.parent = true;
      }

      this.checkedRoles = nextRoles;
    };

    return (
      <MenuItem
        key={role}
        className={cx('OrgRoleDropdownMenu-item', { 'OrgRoleDropdownMenu-item-hover': !disabled })}
        onClick={disabled ? () => {} : handleClick}>
        <div className="OrgRoleDropdownMenu-item-inner">
          <MediaObject gutter="small" center>
            <Image className="OrgRoleDropdownMenu-item-image" src={icon} />
            <Text
              className="OrgRoleDropdownMenu-item-label"
              color={checked ? 'chalkboard' : 'inherit'}>
              {roleName}
            </Text>
          </MediaObject>
          {checked && <Glyph name="check" size="medium" color="mascot" />}
          {disabled && <div className="OrgRoleDropdownMenu-item-disabled" />}
        </div>
      </MenuItem>
    );
  }

  render() {
    const { showConfirmModal } = this.state;
    return (
      <div>
        {!showConfirmModal && (
          <Menu {...this.props} className="OrgRoleDropdownMenu" onRequestHide={this.handleClose}>
            {this.props.potentialRoles.map(({ role, display_role }) => {
              return this.renderMenuItem(role, display_role, roleToIcon[role]);
            })}
          </Menu>
        )}
        <Modal isOpen={showConfirmModal}>
          <ConfirmDialog
            eventScreenName="OrgRoleDropdownMenu"
            title={'Are you sure you want to change your role?'}
            actionButtonText="Yes"
            cancelButtonText="No"
            onConfirm={this.commitPatchRole}
            onCancel={this.revertPatchRole}
          />
        </Modal>
      </div>
    );
  }
}

export default OrgRoleDropdownMenu;
